/**
 * Pure drag-and-drop logic for the Ukeplanlegger's Slice 4: dragging a
 * recipe card from the feed onto a day in `MealPlannerPanel`. Kept pure +
 * exported (no dnd-kit runtime needed) so the id encoding, drop resolution,
 * and the drag-state CSS-class contract are unit-testable — jsdom cannot
 * simulate a real pointer drag (see `SectionsEditor`'s
 * `moveOnDragOver`/`moveOnDragEnd`/… for the same pattern in this codebase).
 * The dnd-kit wiring itself (in `HomePage`/`RecipeCard`/`MealPlannerPanel`)
 * is thin glue verified in the browser.
 *
 * The one exception is `snapDragPreviewToCursor` below: it's a `Modifier`
 * function shaped by dnd-kit's own type, and it leans on
 * `@dnd-kit/utilities`' `getEventCoordinates` (a plain coordinate-extraction
 * helper, not the DndContext runtime) — still a pure function of its
 * arguments, so still directly unit-testable with fake events, no real
 * pointer drag needed.
 */

import {
  closestCenter,
  pointerWithin,
  type CollisionDetection,
  type Modifier,
} from "@dnd-kit/core";
import { getEventCoordinates } from "@dnd-kit/utilities";

const RECIPE_DRAG_PREFIX = "recipe-card:";
const DAY_DROP_PREFIX = "planner-day:";

/** The minimal recipe payload a drag carries — just what's needed to show a
 * drag preview and optimistically render the planned meal before the POST
 * resolves (mirrors the fields `AddToCalendarDialog`'s flow ultimately needs). */
export interface MealPlanDropRecipe {
  id: string;
  title: string;
  /** The recipe's own base servings, used as the planned meal's default (or
   * null if the recipe has none set — same fallback as `AddToCalendarDialog`). */
  servings: number | null;
  heroImageUrl?: string | null;
}

/** The dnd-kit draggable id for a recipe card in the feed. */
export function recipeDragId(recipeId: string): string {
  return `${RECIPE_DRAG_PREFIX}${recipeId}`;
}

/** The recipe id encoded in a recipe-card draggable id, or null if `id` isn't one. */
export function recipeIdFromDragId(id: string): string | null {
  return id.startsWith(RECIPE_DRAG_PREFIX) ? id.slice(RECIPE_DRAG_PREFIX.length) : null;
}

/** The dnd-kit droppable id for a planner day, keyed by its absolute `YYYY-MM-DD` date. */
export function dayDropId(date: string): string {
  return `${DAY_DROP_PREFIX}${date}`;
}

/** The absolute date encoded in a planner-day droppable id, or null if `id` isn't one. */
export function dateFromDayDropId(id: string): string | null {
  return id.startsWith(DAY_DROP_PREFIX) ? id.slice(DAY_DROP_PREFIX.length) : null;
}

/**
 * What to plan, resolved from a drag/drop pair of dnd-kit ids — or null when
 * the drop isn't a "recipe card onto a day" drop (wrong id shapes, no
 * `over`, dropped outside any day, …). Always re-derives `plannedDate` from
 * the CURRENT `overId` rather than any earlier-captured value, so navigating
 * to a different week mid-session can never leave a stale date behind (the
 * day's droppable id IS that day's live date at drop time).
 */
export function resolveMealPlanDrop(
  activeId: string | null | undefined,
  overId: string | null | undefined,
): { recipeId: string; plannedDate: string } | null {
  if (!activeId || !overId) return null;
  const recipeId = recipeIdFromDragId(activeId);
  const plannedDate = dateFromDayDropId(overId);
  if (!recipeId || !plannedDate) return null;
  return { recipeId, plannedDate };
}

/**
 * CSS state suffix for a planner day while a recipe-card drag is in
 * progress: every valid (droppable, role-permitting) day gets `drop-valid`
 * (the dashed outline); the day currently under the pointer/keyboard focus
 * additionally gets `drop-active` (the stronger solid-highlight + scale
 * treatment) — the explicit usability fix from the design handoff. Neither
 * class applies outside of a drag. Pure so the class contract is
 * unit-testable without a real pointer drag.
 */
export function dayDropStateClass(dragging: boolean, isOver: boolean): string {
  if (!dragging) return "";
  return isOver ? " drop-valid drop-active" : " drop-valid";
}

/**
 * Collision detection for the feed→planner `DndContext`: resolve against
 * the actual pointer position, falling back to element-centre distance only
 * when the pointer isn't usable for the decision.
 *
 * Prod bug: dragging over Friday activated Wednesday — a large, consistent
 * offset. Root cause was the PREVIOUS fix for a related complaint (the
 * preview visually trailing the cursor): it added `snapDragPreviewToCursor`
 * as a `<DragOverlay modifiers={[...]}>` only. That's cosmetic — dnd-kit's
 * collision pipeline lives in `DndContext`, not `DragOverlay`, and the two
 * are computed independently:
 *  - `DndContext` measures `draggingNodeRect` (the ORIGINAL card, before any
 *    drag) and combines it with the raw pointer delta, adjusted only by
 *    `DndContext`'s own `modifiers` prop (none were set), to get
 *    `collisionRect`.
 *  - `DragOverlay`'s `modifiers` prop is applied separately, to its own copy
 *    of the transform, purely for what gets painted on screen.
 * With `closestCenter`, the winner is whichever day's centre is nearest
 * `collisionRect`'s centre — i.e. the ORIGINAL card's centre, not the
 * cursor. A recipe card is tall; grab it near the top edge and the card's
 * centre sits a full row away from the pointer, so a neighbouring day wins.
 * That's the exact reported symptom.
 *
 * The fix has two parts (see `snapDragPreviewToCursor` below for the
 * second):
 *  1. Here: prefer `pointerWithin`, which resolves purely from
 *     `pointerCoordinates` (the live, unmodified pointer position) against
 *     each droppable's rect — it never looks at `collisionRect` at all, so
 *     it's immune to this whole class of drift by construction. Only fall
 *     back to `closestCenter` when `pointerWithin` finds nothing: the
 *     pointer sitting in the gap between day drop-zones, or a
 *     keyboard-driven drag, which has no pointer (`pointerCoordinates` is
 *     always null for it, so `pointerWithin` always returns `[]` and this
 *     always falls through) — the existing lift/move/drop keyboard flow
 *     keeps working exactly as before, unaffected by any of this.
 */
export const pointerWithinOrClosestCenter: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  return pointerCollisions.length > 0 ? pointerCollisions : closestCenter(args);
};

/**
 * `DndContext` modifier that snaps the floating recipe-card preview's
 * centre to the pointer.
 *
 * This is now set as `DndContext`'s (not `DragOverlay`'s) `modifiers` prop
 * — deliberately, to close the exact drift the prod bug exposed: applying
 * it at the `DndContext` level means the SAME corrected transform both (a)
 * feeds `collisionRect`, so the `closestCenter` fallback above resolves
 * against a cursor-centred rect instead of the original card's, and (b) is
 * what `DragOverlay` paints, via dnd-kit's own `ActiveDraggableContext`
 * (`DragOverlay` reads that context as its transform's starting point when
 * it has no `modifiers` of its own — see its doc comment at the call site).
 * One computation, two consumers, so they cannot drift apart again. (Do
 * NOT also pass this to `DragOverlay`'s own `modifiers` — that would apply
 * the cursor-snap a second time, on top of a transform that already has it,
 * and throw the preview off-screen.)
 *
 * Equivalent to `@dnd-kit/modifiers`' `snapCenterToCursor` — that package
 * isn't a dependency here (the repo pins its dnd-kit versions
 * deliberately, and it's not worth a new dependency for one function), so
 * this is a small local implementation built on `getEventCoordinates`,
 * which we already depend on via `@dnd-kit/utilities`.
 *
 * A no-op for a keyboard-initiated drag: keyboard lift/move has no
 * pointer, so `activatorEvent` is a `KeyboardEvent`, which has no
 * clientX/clientY — `getEventCoordinates` returns null for it — and the
 * transform passes through unchanged. Keyboard drag keeps resolving purely
 * against the element, as before.
 */
export const snapDragPreviewToCursor: Modifier = ({
  activatorEvent,
  draggingNodeRect,
  transform,
}) => {
  if (!draggingNodeRect || !activatorEvent) return transform;

  const cursor = getEventCoordinates(activatorEvent);
  if (!cursor) return transform;

  return {
    ...transform,
    x: transform.x + (cursor.x - draggingNodeRect.left) - draggingNodeRect.width / 2,
    y: transform.y + (cursor.y - draggingNodeRect.top) - draggingNodeRect.height / 2,
  };
};
